import React, { ReactNode, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { useAuth } from "../../context/AuthContext";

interface AuthGuardProps {
  children: ReactNode;
  requiredRole?: string | string[]; // Optional: specific role requirement
}

const AuthGuard: React.FC<AuthGuardProps> = ({ children, requiredRole }) => {
  const { isAuthenticated, user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      // Redirect to login with the current path for redirect after login
      navigate(`/login?from=${encodeURIComponent(location.pathname)}`, {
        replace: true,
      });
    }
  }, [isAuthenticated, loading, navigate, location]);

  // Role-based authorization check
  useEffect(() => {
    if (!loading && isAuthenticated && user && requiredRole) {
      const hasRequiredRole = Array.isArray(requiredRole)
        ? requiredRole.includes(user.role)
        : user.role === requiredRole;

      if (!hasRequiredRole) {
        // Redirect to dashboard or unauthorized page
        navigate("/dashboard", { replace: true });
      }
    }
  }, [isAuthenticated, loading, user, requiredRole, navigate]);

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-t-primary-500 border-r-primary-500/30 border-b-primary-500/30 border-l-primary-500/30 rounded-full animate-spin" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // If authenticated (and has required role if specified), render children
  if (
    isAuthenticated &&
    (!requiredRole ||
      (user &&
        (Array.isArray(requiredRole)
          ? requiredRole.includes(user.role)
          : user.role === requiredRole)))
  ) {
    return <>{children}</>;
  }

  // This should not be visible due to the redirect, but just in case
  return null;
};

export default AuthGuard;
